import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCarSide, 
  FaPlane, 
  FaMapMarkerAlt, 
  FaArrowRight,
  FaCheck,
  FaClock,
  FaUsers,
  FaSuitcase,
  FaShieldAlt,
  FaUserTie,
  FaCar
} from 'react-icons/fa';

const AirportPickup = () => {
  const [formData, setFormData] = useState({
    serviceType: 'pickup',
    airport: '',
    flightNumber: '',
    arrivalDate: '',
    arrivalTime: '',
    departureDate: '',
    departureTime: '',
    pickupLocation: '',
    dropoffLocation: '',
    passengers: 1,
    luggage: 1,
    vehicleType: '',
    specialRequests: '',
    fullName: '',
    email: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const nigerianAirports = [
    { 
      code: 'LOS', 
      name: 'Lagos - Murtala Muhammed International Airport',
      city: 'Lagos'
    },
    { 
      code: 'ABV', 
      name: 'Abuja - Nnamdi Azikiwe International Airport',
      city: 'Abuja'
    },
    { 
      code: 'KAN', 
      name: 'Kano - Mallam Aminu Kano International Airport',
      city: 'Kano'
    },
    { 
      code: 'PHC', 
      name: 'Port Harcourt - Port Harcourt International Airport',
      city: 'Port Harcourt'
    },
    { 
      code: 'CBQ', 
      name: 'Calabar - Margaret Ekpo International Airport',
      city: 'Calabar'
    },
    { 
      code: 'ENU', 
      name: 'Enugu - Akanu Ibiam International Airport',
      city: 'Enugu'
    }
  ];

  const vehicleTypes = [
    {
      type: 'economy',
      name: 'Economy Car',
      description: 'Comfortable sedan for 1-3 passengers',
      capacity: '1-3 passengers, 2-3 bags',
      icon: FaCar
    },
    {
      type: 'standard',
      name: 'Standard Car',
      description: 'Spacious sedan for 1-4 passengers',
      capacity: '1-4 passengers, 3-4 bags',
      icon: FaCar
    },
    {
      type: 'suv',
      name: 'SUV',
      description: 'Large SUV for families and groups',
      capacity: '1-6 passengers, 4-6 bags',
      icon: FaCarSide
    },
    {
      type: 'luxury',
      name: 'Luxury Car',
      description: 'Premium vehicle with professional chauffeur',
      capacity: '1-4 passengers, 3-4 bags',
      icon: FaUserTie
    }
  ];

  const serviceTypes = [
    {
      value: 'pickup',
      label: 'Airport Pickup',
      description: 'Pick you up from the airport to your destination',
      icon: FaPlane
    },
    {
      value: 'dropoff',
      label: 'Airport Drop-off',
      description: 'Take you from your location to the airport',
      icon: FaCarSide
    },
    // {
    //   value: 'round-trip',
    //   label: 'Round Trip',
    //   description: 'Airport pickup and later drop-off service',
    //   icon: FaArrowRight
    // }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCountChange = (field, increment) => {
    const newValue = Math.max(1, formData[field] + (increment ? 1 : -1));
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const formDataToSend = {
      ...formData,
      _captcha: false,
      _template: "table",
      _autoresponse: "Thanks for your request! We'll respond shortly.",
      _token: "10d0b5f657f51e47f0b98f7f43832928", // Replace with your actual token
    };
  
    const encoded = new URLSearchParams(formDataToSend).toString();
  
    try {
      await fetch("https://formsubmit.co/10d0b5f657f51e47f0b98f7f43832928", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: encoded,
      });
  
      // Show success message in UI
      setIsSubmitted(true);
  
      // Optionally clear the form
      // setFormData(initialState);
  
      // Hide after a few seconds if needed
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("FormSubmit failed:", error);
      alert("There was a problem submitting the form.");
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 gradient-green overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/3536293/pexels-photo-3536293.jpeg')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <FaCarSide className="text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold">Airport Pickup</h1>
            </div>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Reliable airport transfer services with professional drivers. 
              We track your flight and ensure you're met on time with a comfortable, clean vehicle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-12 shadow-2xl text-center max-w-2xl mx-auto"
            >
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <FaCheck className="text-white text-3xl" />
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-6">Airport Transfer Booked!</h3>
              <p className="text-lg text-gray-600 mb-4">
                Thank you for choosing Yowa Travels for your airport transfer.
              </p>
              <p className="text-green-600 font-semibold text-lg">
                An agent will be with you shortly to confirm your booking and provide driver details!
              </p>
              <div className="mt-8 p-6 bg-green-50 rounded-2xl">
                <p className="text-sm text-green-700">
                  <strong>What's Next?</strong><br />
                  We'll send you driver information, vehicle details, and contact numbers 24 hours before your transfer.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8">
                {/* Service Type Selection */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Service Type</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {serviceTypes.map((service) => {
                      const IconComponent = service.icon;
                      return (
                        <label
                          key={service.value}
                          className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                            formData.serviceType === service.value
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="serviceType"
                            value={service.value}
                            checked={formData.serviceType === service.value}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <IconComponent className="text-green-500 text-3xl mx-auto mb-4" />
                            <h5 className="font-bold text-gray-800 mb-2">{service.label}</h5>
                            <p className="text-sm text-gray-600">{service.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Airport Selection */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaPlane className="text-green-500" />
                    <span>Airport Information</span>
                  </h4>
                  
                  <div>
                    <label className="form-label">Airport *</label>
                    <select
                      name="airport"
                      value={formData.airport}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    >
                      <option value="">Select airport</option>
                      {nigerianAirports.map((airport) => (
                        <option key={airport.code} value={airport.code}>
                          {airport.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Flight Information */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Flight Information</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="form-label">Flight Number (Optional)</label>
                      <input
                        type="text"
                        name="flightNumber"
                        value={formData.flightNumber}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="e.g., AA123, BA456"
                      />
                      <p className="text-sm text-gray-500 mt-2">We'll track your flight for delays</p>
                    </div>
                  </div>

                  {(formData.serviceType === 'pickup' || formData.serviceType === 'round-trip') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="form-label">Arrival Date *</label>
                        <input
                          type="date"
                          name="arrivalDate"
                          value={formData.arrivalDate}
                          onChange={handleInputChange}
                          className="form-input"
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">Arrival Time *</label>
                        <input
                          type="time"
                          name="arrivalTime"
                          value={formData.arrivalTime}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {(formData.serviceType === 'dropoff' || formData.serviceType === 'round-trip') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label">Departure Date *</label>
                        <input
                          type="date"
                          name="departureDate"
                          value={formData.departureDate}
                          onChange={handleInputChange}
                          className="form-input"
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">Departure Time *</label>
                        <input
                          type="time"
                          name="departureTime"
                          value={formData.departureTime}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Pickup/Dropoff Locations */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaMapMarkerAlt className="text-green-500" />
                    <span>Locations</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(formData.serviceType === 'pickup' || formData.serviceType === 'round-trip') && (
                      <div>
                        <label className="form-label">Destination Address *</label>
                        <textarea
                          name="pickupLocation"
                          value={formData.pickupLocation}
                          onChange={handleInputChange}
                          rows="3"
                          className="form-input resize-none"
                          placeholder="Enter complete address with landmarks"
                          required
                        ></textarea>
                      </div>
                    )}

                    {(formData.serviceType === 'dropoff' || formData.serviceType === 'round-trip') && (
                      <div>
                        <label className="form-label">Pickup Address *</label>
                        <textarea
                          name="dropoffLocation"
                          value={formData.dropoffLocation}
                          onChange={handleInputChange}
                          rows="3"
                          className="form-input resize-none"
                          placeholder="Enter complete address with landmarks"
                          required
                        ></textarea>
                      </div>
                    )}
                  </div>
                </div>

                {/* Passengers and Luggage */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaUsers className="text-green-500" />
                    <span>Passengers & Luggage</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h5 className="font-bold text-gray-800">Passengers</h5>
                          <p className="text-sm text-gray-600">Number of travelers</p>
                        </div>
                        <FaUsers className="text-green-500 text-xl" />
                      </div>
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => handleCountChange('passengers', false)}
                          disabled={formData.passengers <= 1}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                        >
                          -
                        </button>
                        <span className="text-xl font-bold text-gray-800 mx-4">
                          {formData.passengers}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCountChange('passengers', true)}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-green-500 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h5 className="font-bold text-gray-800">Luggage Pieces</h5>
                          <p className="text-sm text-gray-600">Large bags/suitcases</p>
                        </div>
                        <FaSuitcase className="text-green-500 text-xl" />
                      </div>
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => handleCountChange('luggage', false)}
                          disabled={formData.luggage <= 1}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                        >
                          -
                        </button>
                        <span className="text-xl font-bold text-gray-800 mx-4">
                          {formData.luggage}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCountChange('luggage', true)}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-green-500 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vehicle Type */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Choose Vehicle Type</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {vehicleTypes.map((vehicle) => {
                      const IconComponent = vehicle.icon;
                      return (
                        <label
                          key={vehicle.type}
                          className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                            formData.vehicleType === vehicle.type
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="vehicleType"
                            value={vehicle.type}
                            checked={formData.vehicleType === vehicle.type}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="flex items-start space-x-4">
                            <IconComponent className="text-green-500 text-3xl flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <h5 className="font-bold text-gray-800 mb-2">{vehicle.name}</h5>
                              <p className="text-gray-600 text-sm mb-2">{vehicle.description}</p>
                              <p className="text-sm text-gray-500 mb-2">{vehicle.capacity}</p>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Special Requests */}
                <div className="mb-8">
                  <label className="form-label">Special Requests (Optional)</label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows="4"
                    className="form-input resize-none"
                    placeholder="Child car seat, wheelchair accessibility, stops along the way, specific route preferences, or any other special requirements..."
                  ></textarea>
                </div>

                {/* Contact Information */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="+234 (0) 900 000 0000"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="btn-yowa-primary text-lg px-12 py-4 inline-flex items-center space-x-3"
                  >
                    <FaCarSide />
                    <span>Book Transfer</span>
                    <FaArrowRight />
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    Secure booking with professional drivers. Payment on completion of service.
                  </p>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* Why Choose Our Transfer Service */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Why Choose Our Airport Transfer?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional, reliable, and comfortable airport transfer service that puts your peace of mind first.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FaClock,
                title: 'Flight Tracking',
                description: 'We monitor your flight status and adjust pickup times automatically for delays or early arrivals.',
                color: 'bg-blue-500'
              },
              {
                icon: FaUserTie,
                title: 'Professional Drivers',
                description: 'Experienced, uniformed drivers with excellent local knowledge and customer service training.',
                color: 'bg-green-500'
              },
              {
                icon: FaShieldAlt,
                title: 'Safe & Insured',
                description: 'All vehicles are fully insured, regularly serviced, and equipped with safety features.',
                color: 'bg-purple-500'
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className={`w-16 h-16 ${feature.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AirportPickup;